/* eslint-disable */
import $ from "jquery"
import _identity from "lodash/identity"
import Promise from "plugins/bluebird/index"
import log from "plugins/loglevel/index"
import circularise from "app/utils/circularise"

// An independent jQuery plugin for making a ticker from JSON data
const DEBUG_ENABLED = false
const debug = (msg: any) => {
  if (DEBUG_ENABLED) { log.debug(msg) }
}

/*
# Render a news ticker
#
# Usage:
#
# In HTML:
#    <script id="mydata" type="application/json">
#    [ { "foo": "hey!" },
#      { "bar": "yo!" } ]
#    </script>
#
# In script:
#    $("#mydata").simple_ticker
#      tick_interval: MARKET_NEWS_TICKER_INTERVAL
#      transit: play_transit_animation
#
# Only the first element in the jQuery collection will be treated as
# the data set.
#
# Options:
#
#   tick_interval:
#     from when an transition starts to the next transition starts
#
#   preprocess_json:
#     a function for preprocessing fresh parsed JSON. Given a single
#     object, it should return an array of objects
#
#   gen_next:
#     a generator which defines the order the things are shown on the
#     ticker. Default to a generator walks the array in-order, circularly.
#
#   transit:
#     function for rendering a transition. This function takes the
#     next data object as parameter.
#
#   promise_jsons:
#     if this is non-empty, and it is a function, the JSON object will be
#     got from this promise. In this case, the data-tag is not used. One
#     should call this plugin like this:
#
#       $().simple_ticker ......
#
#     because the jQuery collection then is no longer used.
#
*/

interface Options {
  promise_jsons: Promise<any>

  tick_interval?: number

  gen_next?:        (data: any) => any
  transit?:         (data: any) => void
  preprocess_json?: (data: any) => any

  promise_timeout?:  number
  max_retry?:        number

  on_too_much_retry?: () => void
}

($.fn as any).simple_ticker = function(options: Options){
  // if it"s an empty selection and there isn"t promise from which we can
  // get the data, return without doing anything
  if (!options.hasOwnProperty("promise_jsons") || this.length !== 0) { return }

  const target = $(this)
  const default_options = {
    tick_interval: 2000,
    transit: () => null, // Function that plays animations, render stuff etc
    preprocess_json: _identity,
    gen_next: (data: any) => circularise( data, 0 ),
    promise_timeout: 3300,
    max_retry: 6,
    on_too_much_retry: () =>
      debug(
        `jQuery simple_ticker: AJAX request failed ${options.max_retry} times, giving up`
      ),
  }


  const opts = Object.assign( {}, default_options, options )
  if (!opts.transit){
    throw new Error("simple_ticker: Unspecified transit function")
  }

  const keep_trying = ( retry_times: number ) => {
    if (retry_times === 0){
      opts.on_too_much_retry()
      return
    }

    let getdata
    if (opts.hasOwnProperty("promise_jsons")){
      getdata = opts.promise_jsons
    } else {
      getdata = Promise.try(() => JSON.parse(target.html()))
    }

    getdata.then(opts.preprocess_json)
    .timeout(opts.promise_timeout, "WP_AJAX_TIMEOUT")
    .then((data: any) => {
      const circular = opts.gen_next( data )

      // Transit once. It should be the caller"s duty to assume that the transit
      // handles initialisation case.
      opts.transit(circular.next().value)

      setInterval(() => {
        opts.transit(circular.next().value)
      }, opts.tick_interval)
    })
    .catch((err: any) => {
      debug(err)
      keep_trying( retry_times - 1 )
    })
    .done()
  }
  return keep_trying( opts.max_retry )
}
